import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AgentQuestion, QuestionAnswer, ToolCard } from '../../types/agent';
import { answersFor, questionsOf } from '../../services/agent/questions';
import { CellText } from './CellLink';

/** The card of the `ask_user` tool (sculpt-api PLAN-questions.md): what the agent could settle neither from
 *  the request nor from the workbook. It is not an approval — there is nothing to apply or discard, only
 *  answers the turn is waiting on.
 *
 *  Every question shows the answer Sculpt would pick itself, already selected, so agreeing with the whole
 *  card is one click; whatever the user does not touch goes back marked `left_to_sculpt` and the agent
 *  decides it, saying so in its reply. Once sent, the card shrinks to one line per question. A place a question or its
 *  why names is a link (`CellText`), so the cells it asks about are one click away. */
export function QuestionCard({ card, onAnswer, onDraft, onCancel, onShowCell, sheets }: {
  card: ToolCard;
  onAnswer: (answers: QuestionAnswer[]) => void;
  onDraft?: (answers: QuestionAnswer[]) => void;
  onCancel?: () => void;
  onShowCell?: (sheet: string, cell: string) => void;
  sheets?: readonly string[];
}) {
  const { t } = useTranslation();
  const questions = questionsOf(card);
  const [given, setGiven] = useState<Record<string, string>>({});
  const [other, setOther] = useState<Record<string, boolean>>({});
  const answered = card.status !== 'awaiting_confirmation';

  // What is half-filled in here is what a typed reply sends along with it: the session cannot read the
  // fields, so the card reports them as they change.
  useEffect(() => {
    if (!answered && questions.length) onDraft?.(answersFor(questions, given));
  }, [given, answered]);

  if (!questions.length) return null;
  // Cancelled rather than answered: one line, no recap of answers that were never given.
  if (card.status === 'cancelled') return <div className="animate-pc-in-enter rounded-md bg-code px-2 py-1.5 text-[12px] text-muted">{t('question.cancelled')}</div>;
  if (answered) return <Recap questions={questions} answers={card.answers ?? []} said={card.answerMessage ?? null} />;

  const set = (id: string, value: string) => setGiven((g) => ({ ...g, [id]: value }));
  const send = () => onAnswer(answersFor(questions, given));

  return (
    <div className="animate-pc-in-enter overflow-hidden rounded-lg border border-line-card bg-white" data-card-status={card.status}>
      <div className="border-b border-line-row px-3 py-[9px] text-[12px] font-medium text-ink">{t('question.title')}</div>
      <div className="flex flex-col divide-y divide-line-row">
        {questions.map((q) => (
          <div key={q.id} className="flex flex-col gap-1.5 px-3 py-2.5">
            <span className="text-[12.5px] leading-[1.45] text-ink"><CellText text={q.text} sheets={sheets} onSelectRange={onShowCell} /></span>
            {q.why && <span className="text-[11px] leading-[1.4] text-muted"><CellText text={q.why} sheets={sheets} onSelectRange={onShowCell} /></span>}
            {q.kind === 'choice' ? (
              <Choices q={q} value={given[q.id]} isOther={!!other[q.id]} onPick={(v) => { setOther((o) => ({ ...o, [q.id]: false })); set(q.id, v); }} onOther={() => { setOther((o) => ({ ...o, [q.id]: true })); set(q.id, ''); }} onType={(v) => set(q.id, v)} t={t} />
            ) : (
              <Field q={q} value={given[q.id] ?? ''} onType={(v) => set(q.id, v)} t={t} />
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 border-t border-line-row p-2">
        <button type="button" onClick={send} className="flex-1 rounded-[5px] bg-accent py-[7px] text-center text-[12.5px] font-medium text-white transition-colors hover:bg-accent-hover">
          {t('question.send')}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="rounded-[5px] border border-[#dcdcd8] px-3 py-[7px] text-[12.5px] text-[#4a4f54] transition-colors hover:bg-hover">
            {t('question.cancel')}
          </button>
        )}
      </div>
    </div>
  );
}

function Choices({ q, value, isOther, onPick, onOther, onType, t }: { q: AgentQuestion; value: string | undefined; isOther: boolean; onPick: (v: string) => void; onOther: () => void; onType: (v: string) => void; t: (k: string) => string }) {
  // Untouched means the suggestion: it is what the card sends back, so it is what the card shows selected.
  const selected = isOther ? null : (value ?? q.suggestion ?? null);
  return (
    <div className="flex flex-col gap-1 pt-0.5">
      {(q.options ?? []).map((o) => (
        <label key={o.label} className="flex cursor-pointer items-start gap-2 text-[12px] text-ink">
          <input type="radio" name={q.id} checked={selected === o.label} onChange={() => onPick(o.label)} className="mt-[3px]" />
          <span className="min-w-0 flex-1">
            {o.label}
            {o.label === q.suggestion && <span className="ml-1.5 text-[11px] text-muted">· {t('question.suggested')}</span>}
          </span>
        </label>
      ))}
      <label className="flex cursor-pointer items-start gap-2 text-[12px] text-ink">
        <input type="radio" name={q.id} checked={isOther} onChange={onOther} className="mt-[3px]" />
        <span>{t('question.other')}</span>
      </label>
      {isOther && (
        <input type="text" autoFocus value={value ?? ''} onChange={(e) => onType(e.target.value)} placeholder={t('question.otherPlaceholder')} className="mt-0.5 w-full rounded-[5px] border border-line-card px-2 py-1 text-[12px] text-ink placeholder:text-fainter focus:border-accent focus:outline-hidden" />
      )}
    </div>
  );
}

function Field({ q, value, onType, t }: { q: AgentQuestion; value: string; onType: (v: string) => void; t: (k: string) => string }) {
  return (
    <div className="flex items-center gap-2 pt-0.5">
      <input
        type="text"
        inputMode={q.kind === 'number' ? 'decimal' : 'text'}
        value={value}
        onChange={(e) => onType(e.target.value)}
        placeholder={q.suggestion ?? t('question.answerPlaceholder')}
        className="min-w-0 flex-1 rounded-[5px] border border-line-card px-2 py-1 text-[12px] text-ink placeholder:text-fainter focus:border-accent focus:outline-hidden"
      />
      {q.kind === 'number' && q.unit && q.unit !== '-' && <span className="shrink-0 text-[11.5px] text-muted">{q.unit}</span>}
    </div>
  );
}

/** What stays in the thread once the card is answered: the question and the answer, one line each, and the
 *  user's own words underneath when they typed instead of filling it in. This is also what a reloaded thread
 *  shows, because the answers travel with the call in the history. */
function Recap({ questions, answers, said }: { questions: AgentQuestion[]; answers: QuestionAnswer[]; said?: string | null }) {
  const { t } = useTranslation();
  const byId = new Map(answers.map((a) => [a.id, a]));
  return (
    <div className="flex animate-pc-in-enter flex-col gap-1 rounded-md bg-code px-2 py-1.5 text-[12px]">
      {questions.map((q) => {
        const a = byId.get(q.id);
        return (
          <div key={q.id} className="flex min-w-0 items-baseline gap-1.5">
            <span className="min-w-0 truncate text-muted" title={q.text}>{q.text}</span>
            <span className="shrink-0 text-faint">→</span>
            <span className="min-w-0 flex-1 truncate text-ink" title={a?.answer || undefined}>
              {a?.answer || '—'}
              {a?.left_to_sculpt && <span className="ml-1.5 text-[11px] text-muted">· {t('question.leftToSculpt')}</span>}
            </span>
          </div>
        );
      })}
      {said && <div className="mt-0.5 border-t border-line-row pt-1 text-[11.5px] text-ink">{said}</div>}
    </div>
  );
}
